import type { AIProvider, GenerateResponse, GenerationParams, AIModel } from '../types';
import { API_BASE_URL } from '../config';
import type { AIPii } from './pii';

/**
 * ProxyService - Communicates with backend API to hide API keys
 * All AI calls go through the secure backend proxy
 */
class ProxyService {
  private baseUrl: string;

  constructor() {
    // In production, this will be /api (same origin)
    // In development, it points to the API server
    this.baseUrl = import.meta.env.VITE_API_URL || API_BASE_URL;
  }

  /**
   * Generate interpretation (non-streaming)
   */
  async generate(
    prompt: string,
    provider: AIProvider,
    model: string,
    options?: {
      thinkingConfig?: { enabled: boolean; budget: number };
      generationParams?: GenerationParams;
      systemPrompt?: string;
      /** Dane z formularza do pseudonimizacji po stronie backendu (wymagane przez backend; `{}` gdy pusto). */
      pii?: AIPii;
    }
  ): Promise<GenerateResponse> {
    const response = await fetch(`${this.baseUrl}/ai/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        prompt,
        provider,
        model,
        thinkingConfig: options?.thinkingConfig,
        generationParams: options?.generationParams,
        systemPrompt: options?.systemPrompt,
        pii: options?.pii ?? {},
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Generation failed');
    }

    return response.json();
  }

  /**
   * Stream interpretation with real-time updates.
   * Pass `options.signal` to support AbortController (Stop button).
   */
  async *stream(
    prompt: string,
    provider: AIProvider,
    model: string,
    options?: {
      thinkingConfig?: { enabled: boolean; budget: number };
      generationParams?: GenerationParams;
      systemPrompt?: string;
      pii?: AIPii;
      signal?: AbortSignal;
    }
  ): AsyncGenerator<{ content?: string; thinking?: string; done?: boolean; error?: string }> {
    const response = await fetch(`${this.baseUrl}/ai/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      signal: options?.signal,
      body: JSON.stringify({
        prompt,
        provider,
        model,
        thinkingConfig: options?.thinkingConfig,
        generationParams: options?.generationParams,
        systemPrompt: options?.systemPrompt,
        pii: options?.pii ?? {},
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Stream failed');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Flush any decoder remainder and complete events left in the buffer
        // (e.g. a final event not followed by a newline / missing [DONE]).
        buffer += decoder.decode();
        for (const rawLine of buffer.split('\n')) {
          const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              yield { error: parsed.error };
              return;
            }
            if (parsed.content) {
              yield { content: parsed.content };
            }
            if (parsed.thinking) {
              yield { thinking: parsed.thinking };
            }
          } catch {
            console.warn('[proxyService] Dropping incomplete SSE data at stream end:', data.substring(0, 100));
          }
        }
        yield { done: true };
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (let i = 0; i < lines.length; i++) {
        // Tolerate CRLF line endings
        const line = lines[i].endsWith('\r') ? lines[i].slice(0, -1) : lines[i];
        if (!line.startsWith('data: ')) continue;

        const data = line.slice(6).trim();

        if (data === '[DONE]') {
          yield { done: true };
          return;
        }

        try {
          const parsed = JSON.parse(data);
          if (parsed.error) {
            yield { error: parsed.error };
            return;
          }
          if (parsed.content) {
            yield { content: parsed.content };
          }
          if (parsed.thinking) {
            yield { thinking: parsed.thinking };
          }
        } catch {
          if (i === lines.length - 1 && buffer === '') {
            // The JSON is truncated at a network chunk boundary. Put the
            // ORIGINAL line back as the (incomplete) buffer tail — without an
            // extra newline — so the next chunk is appended directly to it
            // and the event can be re-parsed as a whole.
            buffer = line;
          } else {
            // A complete line with malformed JSON cannot be fixed by future
            // chunks — skip it instead of corrupting the buffer.
            console.warn('[proxyService] Skipping malformed SSE data:', data.substring(0, 100));
          }
        }
      }
    }
  }

  /**
   * Get available models for a provider
   */
  async getModels(provider: AIProvider): Promise<AIModel[]> {
    const response = await fetch(`${this.baseUrl}/ai/models/${provider}`, {
      credentials: 'same-origin',
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return (data.models || []).map((m: AIModel) => ({ ...m, provider }));
  }

  /**
   * Test connection to a provider
   */
  async testConnection(provider: AIProvider): Promise<{ connected: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/ai/test/${provider}`, {
        method: 'POST',
        credentials: 'same-origin',
      });

      return response.json();
    } catch (error) {
      return {
        connected: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      };
    }
  }

  /**
   * Get list of configured providers
   */
  async getProviders(): Promise<Array<{ id: AIProvider; name: string; configured: boolean }>> {
    try {
      const response = await fetch(`${this.baseUrl}/providers`, {
        credentials: 'same-origin',
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.providers || [];
    } catch {
      return [];
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp?: string }> {
    const response = await fetch(`${this.baseUrl.replace(API_BASE_URL, '')}/health`, {
      credentials: 'same-origin',
    });
    return response.json();
  }
}

export const proxyService = new ProxyService();
export default proxyService;
